import { BaseButton, ClaimCloseoutType, CloseoutForm, CloseoutFormValues, getOptions, ISelectOption } from '@enter/core';
import React, { useMemo, useState } from 'react';
import { ClaimStore } from 'stores';
import { ProviderClaimCloseoutDescription } from './closeout-dialog';
import {
    ClaimCloseoutAttemptResult,
    ClaimCloseoutBatchTarget,
    partitionCloseoutResults,
    runBatchCloseout,
} from './batch-closeout.util';
import * as S from './batch-closeout.styles';

interface BatchProgress {
    completed: number;
    total: number;
}

const initialValues: CloseoutFormValues = {
    closeoutType: ClaimCloseoutType.CLOSED,
    closeoutDescription: undefined,
    closeoutVoid: false,
};

export const BatchProviderCloseoutDialog: React.FC<{
    targets: ClaimCloseoutBatchTarget[];
    onCancel: () => void;
    onComplete?: () => void;
}> = ({ targets, onCancel, onComplete }) => {
    const descriptionOptions: ISelectOption[] = useMemo(
        () => getOptions(ProviderClaimCloseoutDescription, { normalize: true }),
        [],
    );
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState<BatchProgress | undefined>(undefined);
    const [results, setResults] = useState<ClaimCloseoutAttemptResult[] | undefined>(undefined);
    const [submittedValues, setSubmittedValues] = useState<CloseoutFormValues | undefined>(undefined);

    const partitioned = results ? partitionCloseoutResults(results) : undefined;

    const runAgainst = async (
        runTargets: ClaimCloseoutBatchTarget[],
        values: CloseoutFormValues,
    ): Promise<ClaimCloseoutAttemptResult[]> => {
        setRunning(true);
        setProgress({ completed: 0, total: runTargets.length });
        try {
            return await runBatchCloseout(
                runTargets,
                values,
                (id, payload) => ClaimStore.update(id, payload),
                10,
                (completed, total) => setProgress({ completed, total }),
            );
        } finally {
            setRunning(false);
        }
    };

    const handleSubmit = async (values: CloseoutFormValues): Promise<void> => {
        setSubmittedValues(values);
        const nextResults = await runAgainst(targets, values);
        setResults(nextResults);
        if (nextResults.every(result => result.ok)) {
            onComplete?.();
        }
    };

    const handleRetry = async (): Promise<void> => {
        if (!results || !submittedValues) {
            return;
        }

        const failedIds = new Set(results.filter(result => !result.ok).map(result => result.claimId));
        if (failedIds.size === 0) {
            return;
        }

        const retryTargets = targets.filter(target => failedIds.has(target.id));
        const retryResults = await runAgainst(retryTargets, submittedValues);
        const retryById = new Map(retryResults.map(result => [result.claimId, result]));
        const merged = results.map(result => retryById.get(result.claimId) ?? result);

        setResults(merged);
        if (merged.every(result => result.ok)) {
            onComplete?.();
        }
    };

    if (results && partitioned) {
        const actionVerb = submittedValues?.closeoutType === ClaimCloseoutType.OPEN ? 'reopened' : 'closed out';
        return (
            <S.DialogBody>
                <S.Status>
                    {partitioned.succeeded.length} of {results.length} claim{results.length === 1 ? '' : 's'}{' '}
                    {actionVerb}.
                </S.Status>
                {partitioned.failed.length > 0 && (
                    <>
                        <S.ErrorText>
                            {partitioned.failed.length} claim{partitioned.failed.length === 1 ? '' : 's'} failed.
                            Retry those without re-running the ones that succeeded.
                        </S.ErrorText>
                        <S.FailureList>
                            {partitioned.failed.map(result => (
                                <S.FailureItem key={result.claimId}>
                                    Claim {result.claimId}: {result.error}
                                </S.FailureItem>
                            ))}
                        </S.FailureList>
                    </>
                )}
                <S.Actions>
                    <BaseButton variant="outlined" size="medium" onClick={onCancel} disabled={running}>
                        Close
                    </BaseButton>
                    {partitioned.failed.length > 0 && (
                        <BaseButton
                            variant="primary"
                            size="medium"
                            onClick={() => void handleRetry()}
                            disabled={running}
                        >
                            Retry failed
                        </BaseButton>
                    )}
                </S.Actions>
            </S.DialogBody>
        );
    }

    return (
        <S.DialogBody>
            <S.Note>
                Closing out {targets.length} claim{targets.length === 1 ? '' : 's'}. A claim that is already voided
                cannot be reopened by this action — it is reported as a failure instead of silently skipped.
            </S.Note>
            {running && progress && (
                <S.Status>
                    {progress.completed} of {progress.total} done…
                </S.Status>
            )}
            <CloseoutForm
                initialValues={initialValues}
                descriptionOptions={descriptionOptions}
                onSubmit={handleSubmit}
                onCancel={onCancel}
            />
        </S.DialogBody>
    );
};
